import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { postRequest } from "api/tools/httpRequests";
import { AxiosResponse } from "axios";
import { IDBActivityCart } from "commonFnBcode/types/activityCart.types";
import {
  ISharingBasisEnum,
  sharingBasisEnum,
  TransferTypeEnum,
  transferTypePickupDropEnum,
} from "commonFnBcode/types/dmc.types";
import { IJSONResponse } from "components/afterLogin/dashboard/flightApis/AirlineDiscountMarkup/types";
import { ca } from "date-fns/locale";
import { IJobsheetDateRangeState } from "dmcMaster/jobSheet/DmcJobsheet";
import moment from "moment";
import { toast } from "react-toastify";
import { RootState } from "redux/store";

interface IDateRange {
  from: Date;
  to: Date;
}

const initialState: {
  cartData: IDBActivityCart | undefined;
  cartLoadingIndex: number[];
  fetchCartLoading: boolean;
} = {
  cartData: undefined,
  cartLoadingIndex: [],
  fetchCartLoading: false,
};

// thunk

interface ISearchParams {
  dateRange: IJobsheetDateRangeState;
  selectedCity: number;

  passengers: {
    adults: number;
    children: number;
    infants: number;
  };
}

const fetchCartData = createAsyncThunk<
  void,
  {
    cartId?: number;
    updateCartParams?: {
      dateRange: IDateRange;
      passengers: {
        adults: number;
        children: number;
      };
    };
  },
  {
    state: RootState;
  }
>("activityCart/fetchCartData", async (props, thunkAPI) => {
  thunkAPI.dispatch(activityCartActions.setCartLoading(true));

  try {
    let cartId = props.cartId;

    if (!cartId) {
      const reduxState = thunkAPI.getState();
      cartId = reduxState.activityCart.cartData?.id;
    }

    const response: AxiosResponse<IJSONResponse<IDBActivityCart>> =
      await postRequest(`activities/cart/getCartData`, {
        cartId: cartId,
      });

    if (response.data.result) {
      thunkAPI.dispatch(activityCartActions.setCartData(response.data.result));
    }
    // thunkAPI.dispatch(
    //   activityCartActions.setSearchResults({
    //     activities: response.data.result.activities,
    //   })
    // );

    // return response;
  } catch (error) {
    toast.error("Error fetching activity search");
  }
  thunkAPI.dispatch(activityCartActions.setCartLoading(false));
});

const deleteCartItem = createAsyncThunk<
  void,
  {
    cartId: number;
    cartItemId: number;
  },
  {
    state: RootState;
  }
>("activityCart/deleteCartItem", async (props, thunkAPI) => {
  try {
    const response: AxiosResponse<IJSONResponse> = await postRequest(
      `activities/cart/deleteCartItem`,
      props
    );

    if (response.data.success) {
      thunkAPI.dispatch(
        fetchCartData({
          cartId: response.data.result.cartId,
        })
      );
    }
  } catch (error: any) {
    toast.error(error.message);
  }
});

const clearAllCart = createAsyncThunk<
  void,
  {
    cartId: number;
  },
  {
    state: RootState;
  }
>("activityCart/clearAllCart", async (props, thunkAPI) => {
  try {
    const response: AxiosResponse<IJSONResponse> = await postRequest(
      `activities/cart/clearAllCart`,
      props
    );

    if (response.data.success) {
      thunkAPI.dispatch(
        fetchCartData({
          cartId: response.data.result.cartId,
        })
      );
    }
  } catch (error: any) {
    toast.error(error.message);
  }
});

const addToCart = createAsyncThunk<
  void,
  {
    loadingId: number; // we can pass activity id or transfer id
    tourOptionId?: number;
    pickUp?: number | null;
    drop?: number | null;
    returnId?: number | null;
  },
  {
    state: RootState;
  }
>("activityCart/addToCart", async (props, thunkAPI) => {
  try {
    thunkAPI.dispatch(
      activityCartActions.setAddToCartLoading({
        type: "add",
        value: props.loadingId,
      })
    );
    const reduxState = thunkAPI.getState();
    const response = await postRequest(`activities/cart/addToCart`, {
      ...props,
      cartId: reduxState.activityCart.cartData?.id,
      adults: reduxState.activitySearch.passengers.adults,
      children: reduxState.activitySearch.passengers.children,
      startDate: moment(reduxState.activitySearch.dateRange.from).format(
        "YYYY-MM-DD"
      ),
      endDate: moment(reduxState.activitySearch.dateRange.to).format(
        "YYYY-MM-DD"
      ),
    });

    if (response.data.result.cartId) {
      thunkAPI.dispatch(
        fetchCartData({
          cartId: response.data.result.cartId,
        })
      );
    }

    // return response;
  } catch (error: any) {
    toast.error(error.message);
  }

  toast.success("Successfully, Added to cart");
  thunkAPI.dispatch(
    activityCartActions.setAddToCartLoading({
      type: "remove",
      value: props.loadingId,
    })
  );
});

const duplicateCart = createAsyncThunk<
  void,
  IDBActivityCart,
  {
    state: RootState;
  }
>("activityCart/duplicateCart", async (cart, thunkAPI) => {
  try {
    const cartItems = cart.cartItems.map((item) => {
      return {
        tourOptionId: item.tourOptionId,
        pickUp: item.pickUpAreaTransfer?.id,
        drop: item.dropAreaTransfer?.id,
        returnId: item.returnAreaTransfer?.id,
      };
    });
    const response = await postRequest(`activities/cart/duplicateCart`, {
      cartItems,
      adults: cart.adults,
      children: cart.children,
      startDate: cart.startDate,
      endDate: cart.endDate,
    });

    if (response.data.result.cartId) {
      await thunkAPI.dispatch(
        fetchCartData({
          cartId: response.data.result.cartId,
        })
      );
    }
    // return response;
  } catch (error: any) {
    toast.error(error.message);
  }
});

export const ActivityCartSlice = createSlice({
  name: "ActivityCart",
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    setCartData: (
      state,
      action: PayloadAction<IDBActivityCart | undefined>
    ) => {
      state.cartData = action.payload;
    },
    clearActiveCart: (state) => {
      state.cartData = undefined;
    },
    setCartLoading: (state, action: PayloadAction<boolean>) => {
      state.fetchCartLoading = action.payload;
    },
    setAddToCartLoading: (
      state,
      action: PayloadAction<{
        type: "add" | "remove" | "migrate";
        value: number;
      }>
    ) => {
      action.payload.type === "migrate"
        ? (state.cartLoadingIndex = [])
        : action.payload.type === "add"
        ? state.cartLoadingIndex.push(action.payload.value)
        : state.cartLoadingIndex.splice(
            state.cartLoadingIndex.indexOf(action.payload.value),
            1
          );
    },
  },
});

export const activityCartActions = ActivityCartSlice.actions;

export const activityCartThunks = {
  fetchCartData,
  addToCart,
  deleteCartItem,
  clearAllCart,
  duplicateCart,
};

export const selectActivityCartSlice = (state: RootState) => {
  return state.searchFlight;
};

export default ActivityCartSlice.reducer;
